
import React, { useState } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { Shield, CheckCircle, AlertOctagon, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from '@/config/api';
import { useLanguage } from '@/contexts/LanguageContext';

const Verify = () => {
  const { toast } = useToast();
  const { user, token, refreshUserData } = useAuth();
  const navigate = useNavigate();
  
  const [validationCode, setValidationCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t, locale } = useLanguage();

  const handleResendCode = async () => {
    setIsResending(true);
    try {
      const response = await fetch(`${BASE_URL}/api/welcome`, {
        method: 'POST',
        headers: {
          'Accept' : 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
  
      if (response.ok) {
        toast({
          title: t('verify.codeSent'),
          description: t('verify.codeSentDesc'),
        });
      } else {
        const errorData = await response.json();
        toast({
          title: t('verify.resendFailed'),
          description: errorData.message || t('verify.resendFailedDesc'),
          variant: "destructive"
        });
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "An error occurred while resending the code.",
        variant: "destructive"
      });
    } finally {
      setIsResending(false);
    }
  };
  
  
  const handleValidateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsValidating(true);
    setError(null);
    
    try {
      const response = await fetch(`${BASE_URL}/api/user/validate`, {
        method: 'POST',
        headers: {
          'Accept' : 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ validation_code: validationCode })
      });
      
      if (response.ok) {
        const data = await response.json();

        if(data.status === 1) {
          await refreshUserData();
          toast({
            title: t('verify.accountValidated'),
            description: t('verify.accountValidatedDesc'),
          });
          navigate('/agenda');
        } else {
          setError(t('verify.invalidCode'));
          toast({
            title: t('verify.validationFailed'),
            description: t('verify.invalidCode'),
            variant: "destructive"
          });
        }
      } else {
        const errorData = await response.json();
        setError(errorData.message || t('verify.invalidCode'));
        toast({
          title: t('verify.validationFailed'),
          description: errorData.message || "Invalid validation code",
          variant: "destructive"
        });
      }
    } catch (error) {
      setError("An error occurred during validation. Please try again.");
      toast({
        title: "Validation error",
        description: "An error occurred during validation",
        variant: "destructive"
      });
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 container py-8 mt-16">
        <div className="max-w-md mx-auto mt-8">
          <Card className="border-2 border-primary/20">
            <CardHeader className="text-center pb-4">
              <div className="mx-auto bg-primary/10 w-16 h-16 flex items-center justify-center rounded-full mb-4">
                <Shield className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="text-2xl">{t('verify.verifyAccount')}</CardTitle>
              <CardDescription>
                {t('verify.verifyAccountDesc')}
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              {error && (
                <div className="bg-destructive/10 border border-destructive/30 rounded-md p-3 mb-4 flex items-center gap-2">
                  <AlertOctagon className="h-5 w-5 text-destructive" />
                  <p className="text-destructive text-sm">{error}</p>
                </div>
              )}
              
              <form onSubmit={handleValidateAccount} className="space-y-4">
                <div className="space-y-2">
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder={t('verify.placeHolder')}
                    value={validationCode}
                    onChange={(e) => setValidationCode(e.target.value)}
                    className="text-center text-lg tracking-widest"
                    maxLength={6}
                  />
                  <p className="text-xs text-muted-foreground text-center pt-3">
                    {t('verify.enterCode')}
                  </p>
                </div>
                
                <Button 
                  type="submit"
                  className="w-full"
                  disabled={isValidating || !validationCode}
                >
                  {isValidating ? (
                    <div className="flex items-center justify-center">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      <span> {t('verify.bntValidatingAccount')}</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <CheckCircle className="mr-2 h-4 w-4" />
                      <span> {t('verify.bntVerifyAccount')}</span>
                    </div>
                  )}
                </Button>
              </form>
              
              <div className="mt-6 text-center text-sm text-muted-foreground">
                <p>
                  {isResending ? '' : t('verify.doNotReceiveCode')}
                  <Button 
                    variant="link" 
                    className="px-1.5" 
                    onClick={handleResendCode}
                    disabled={isResending}
                  >
                    {isResending ? (
                      <>
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        {t('verify.btnSendingCode')}...
                      </>
                    ) : (
                     t('verify.resendCode')
                    )}
                  </Button>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Verify;
